import { HttpService } from '@/services/http.ts'
import { DashboardFilters } from '@/services/dashboard-filters.ts'

export type RecentPageKind =
  | 'customer'
  | 'account'
  | 'audience'
  | 'category'
  | 'competitor'
  | 'branch'
  | 'relationship_manager'
  | 'agent_conversation'
  | 'page'

/** What the frontend reports when a dashboard page is opened. */
export interface RecentPageVisit {
  kind: RecentPageKind
  /** entity id for entity kinds, the route path for static pages */
  key: string
  /** SPA route that reopens the page */
  path: string
  /** only used for static pages; entity titles are resolved by the backend */
  title?: string
}

/** One entry of the "Continue your work" card. */
export interface RecentPage {
  id: number
  kind: RecentPageKind
  kind_label: string
  key: string
  path: string
  title: string
  subtitle: string
  metadata: Record<string, unknown> | null
  visit_count: number
  last_visited_at: string
}

/** The card shows this many by default and up to RECENT_PAGES_MAX on "Show all". */
export const RECENT_PAGES_DEFAULT = 10
export const RECENT_PAGES_MAX = 15

// The same page reported again within this window is ignored (remounts, double
// navigations) so one visit counts once.
const VISIT_DEDUPE_MS = 30_000
const recentlyVisited = new Map<string, number>()

function roleHeaders(): Record<string, string> | null {
  try {
    const role = DashboardFilters.getCurrentRole()
    return role ? { Role: role.id } : null
  } catch {
    return null
  }
}

export class RecentPagesApi {
  /** Recently visited pages of the current role, newest first. */
  static async list(limit: number = RECENT_PAGES_MAX): Promise<RecentPage[]> {
    return HttpService.get<RecentPage[]>(`/activity/recent/?limit=${limit}`, { Role: DashboardFilters.getCurrentRole().id })
  }

  /**
   * Report a page visit. Fire-and-forget: never throws, never shows a toast, never
   * blocks navigation. Resolves true when a request was sent.
   */
  static visit(visit: RecentPageVisit): boolean {
    const headers = roleHeaders()
    if (!headers) return false

    const key = `${headers.Role}:${visit.kind}:${visit.key}`
    const now = Date.now()
    for (const [visitedKey, visitedAt] of recentlyVisited) {
      if (now - visitedAt >= VISIT_DEDUPE_MS) recentlyVisited.delete(visitedKey)
    }
    if (recentlyVisited.has(key)) return false
    recentlyVisited.set(key, now)

    HttpService.postQuiet('/activity/recent/', visit, headers).catch(() => {
      // tracking must never affect the user; allow a retry on the next visit
      recentlyVisited.delete(key)
    })
    return true
  }

  static async remove(id: number): Promise<void> {
    await HttpService.delete(`/activity/recent/${id}/`, { Role: DashboardFilters.getCurrentRole().id })
  }
}
